/**
 * Pick the tournament-status row that a tournament surface should render.
 *
 * The gotTnmStatus array can hold MORE THAN ONE row for the same tournament: a
 * not-registered lobby viewer's row is copied from whichever player is currently
 * first in the server's list, so its playerId changes between pushes and the
 * reducer APPENDS a new row instead of updating in place — leaving a stale 'R'
 * row sitting in front of a fresh 'P' one. Taking filtered[0] then showed the
 * stale row (lobby stuck on "Registering" until a refresh).
 *
 * Selection rules:
 *  - Prefer the viewer's OWN row (playerId === guid) — their personal status.
 *  - Otherwise take the freshest by server time, tie-broken to the most recently
 *    merged (last) row.
 *
 * `elapsedTime` can arrive as a formatted string ("01:23:45") or the literal
 * "Finished" — Number() then yields NaN, and because `NaN >= NaN` is false the
 * reduce would freeze on filtered[0] (the OLDEST row) forever while fresh rows
 * keep appending behind it (the "panel never updates even though gotTnmStatus
 * keeps arriving" bug). Any non-finite freshness is coerced to 0 so the tie-break
 * falls through to merge order and the reduce ends on the freshest row.
 *
 * Lives here rather than in TournamentDetails (which re-exports it) so the felt
 * can read the same row without importing the whole details dashboard.
 */
export function pickLiveTournamentRow(
  rows: any,
  tournamentId: any,
  guid: any
): { row: any; count: number; usedOwn: boolean } | null {
  const filtered = rowsOfTournament(rows, tournamentId);
  if (filtered.length === 0) return null;
  const own = filtered.find((d: any) => d?.playerId === guid);
  const row = own ?? newestOf(filtered);
  return { row, count: filtered.length, usedOwn: !!own };
}

/** A row's server time, 0 when it has none usable (see above). */
export const tournamentRowFreshness = (row: any): number => {
  const n = Number(row?.time ?? row?.serverTime ?? row?.elapsedTime);
  return Number.isFinite(n) ? n : 0;
};

/** The freshest row for the tournament, whoever it belongs to; null if none. */
export const pickNewestTournamentRow = (rows: any, tournamentId: any): any | null => {
  const filtered = rowsOfTournament(rows, tournamentId);
  return filtered.length === 0 ? null : newestOf(filtered);
};

const rowsOfTournament = (rows: any, tournamentId: any): any[] =>
  (Array.isArray(rows) ? rows : []).filter(
    (d: any) => Number(d?.tournamentId) === Number(tournamentId)
  );

// Freshest by server time, tie-broken to the most recently merged (last) row.
const newestOf = (rows: any[]) =>
  rows.reduce((best: any, cur: any) =>
    tournamentRowFreshness(cur) >= tournamentRowFreshness(best) ? cur : best
  );
